import { useEffect, useState } from "react";

import { PUSH_UPDATE } from "@/shared/constants/routes";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { matchPath, useLocation } from "react-router-dom";

import { Media } from "@/features/push/domain/models/media";
import { PushPrice, PushTarget } from "@/features/push/domain/models/push";
import {
  useGetPushByIdQuery,
  usePricesQuery
} from "@/features/push/presentation/viewModel";

import { MymCloudContext } from "./contexts/MymCloudContext";
import { pushFormValidation } from "./pages/setup/formValidation";

export type PushFormMedia = {
  url: string;
  file?: File;
  preview?: string;
  id?: string;
  isLoading?: boolean;
  type?: string; // e.g., "image", "video"
  duration?: number;
};

export interface PushFormProps {
  id?: string;
  targets: PushTarget[];
  teaser: PushFormMedia | null;
  medias: PushFormMedia[];
  caption: string;
  isScheduled: boolean;
  scheduledAt: Date | null;
  mod: boolean;
  playlists: string[];
  price?: PushPrice;
  customPrice?: PushPrice;
  hasDiscount?: boolean;
  discount?: {
    price: PushPrice;
    duration: number;
  } | null;
  thirdPartyConsent: boolean;
}

export const defaultPushFormValues = {
  targets: [],
  teaser: null,
  medias: [],
  caption: "",
  isScheduled: false,
  scheduledAt: null,
  mod: false,
  playlists: [],
  price: {
    value: 0,
    currency: "EUR" // Default currency, can be changed as needed
  },
  customPrice: {
    value: 0,
    currency: "EUR" // Default currency, can be changed as needed
  },
  hasDiscount: false,
  discount: {
    price: {
      value: 0
    },
    duration: 0
  },
  thirdPartyConsent: false
};

export default function PushFormProviderWrapper({ children }: any) {
  const location = useLocation();
  const matchedRoutes = matchPath(`/push${PUSH_UPDATE}`, location.pathname);
  const id = matchedRoutes ? location.pathname.split("/").slice(-2, -1)[0] : "";

  const selectedMedias: Media[] = location.state?.selectedMedias || [];

  const { data: push, isLoading: isLoadingPush } = useGetPushByIdQuery(id);
  const { data: prices, isLoading: isLoadingPrices } = usePricesQuery();

  const defaultValues = push
    ? {
        id: push.id,
        targets: push.targets,
        teaser: push.teaser,
        medias: push.medias,
        caption: push.caption,
        isScheduled: !!push.scheduledAt,
        scheduledAt: new Date(push.scheduledAt),
        mod: push.mod,
        playlists: push.playlists,
        price: {
          value: push.price.value / 100, // Assuming price is stored in cents
          currency: push.price.currency || "EUR" // Default to EUR if not provided
        },
        // set custom price if price.value is not one of the predefined values
        customPrice: prices?.ranges.some(
          (price) => price.value === push.price.value
        )
          ? defaultPushFormValues.customPrice
          : {
              value: push.price.value / 100, // Assuming price is stored in cents
              currency: push.price.currency || "EUR" // Default to EUR if not provided
            },
        hasDiscount: !!push.discount,
        discount: {
          price: {
            value: push.discount?.price.value / 100 || 0, // Assuming discount price is stored in cents
            currency: push.discount?.price.currency || "EUR" // Default to EUR if not provided
          },
          duration: push.discount?.duration || 0
        },
        thirdPartyConsent: push.thirdPartyConsent || false
      }
    : {
        ...defaultPushFormValues,
        medias: selectedMedias.map((media) => ({
          id: media.id,
          url: media.url,
          preview: media.preview,
          type: media.type,
          duration: media.duration
        }))
      };

  const methods = useForm<PushFormProps>({
    criteriaMode: "all",
    defaultValues: defaultValues,
    mode: "onSubmit",
    resolver: yupResolver(pushFormValidation)
  });

  const [mymCloudSelectedMedias, setMymCloudSelectedMedias] = useState<Media[]>(
    []
  );

  useEffect(() => {
    methods.reset(defaultValues);
  }, [isLoadingPrices, isLoadingPush]);

  return (
    <MymCloudContext.Provider
      value={{
        mymCloudSelectedMedias: mymCloudSelectedMedias,
        setMymCloudSelectedMedias: setMymCloudSelectedMedias
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </MymCloudContext.Provider>
  );
}
